import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, User, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getDoc, doc, onSnapshot } from 'firebase/firestore';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from './State/store';
import { setUser, clearAuthCache, updateSubscriptionStatus, selectUsers } from './State/usersSlice';
import { db } from './firebase/config';
import SchoolWebsite from './components/SchoolWebsite';
import SchoolWebsitePreview from './components/SchoolWebsitePreview';
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { recaptchaKey } from './firebase/config';
import PageLoader from './authentication/PageLoader';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionGuard from './components/SubscriptionGuard';
import RoleBasedRoute from './components/RoleBasedRoute';

import Authe from './authentication/Auth';
import Signup from './authentication/Signup';
import Layout from './components/Layout';
import Teacherdashboard from './Teacherdashboard/Teacherdashboard';
import TeacherLibraryManagement from './Teacherdashboard/TeacherLibraryManagement';
import Enrolltoaschool from './Teacherdashboard/Timetable/Enrolltoschool/Enrolltoaschool';
import PrivacyPolicy from './components/requiredlegaldocuments/PrivacyPolicy';
import TermsOfService from './components/requiredlegaldocuments/TermsOfService';
import Messageallteachers from './Teacherdashboard/communication/Messageallteachers';
import Home from './components/requiredlegaldocuments/landing/Home';
import StaffSettings from './Teacherdashboard/StaffSettings';
import TeachersManagement from './components/TeacherManagement';
import DormsManagement from './components/DormsManagement';
import CommunicationManagement from './components/CommunicationManagement';
import StudentsManagement from './components/StudentsManagement';
import ClassesManagement from './components/ClassesManagement';
import SettingsContent from './components/SettingsManagement';
import LibrarianRoute from './components/LibrarianRoute';
import SchoolRegistrationForm from './components/forms/SchoolRegistrationForm';
import EditSchoolDetails from './components/forms/EditSchoolDetails';
import AdminRegistrationForm from './components/forms/AdminRegistrationForm';
import SchoolAlumni from './components/forms/SchoolAlumni';
import GuardianLogin from './authentication/GuardianLogin';
import StudentLogin from './authentication/StudentLogin';
import StudentDashboard from './Studentfile/StudentDashboard';
import GuardianDashboard from './Parentdashboard/GuardianDashboard';
import TeacherCommunication from './Teacherdashboard/TeacherCommunication';
import ParentCommunication from './Parentdashboard/ParentCommunication';
import ParentSettings from './Parentdashboard/ParentSettings';
import StudentSettings from './Studentfile/StudentSettings';
import StudentCommunication from './Studentfile/StudentCommunication';
import AdminAttendanceAnalytics from './components/AdminAttendanceAnalytics';
import AttendanceManagement from './Teacherdashboard/AttendanceTaking';
import StudentAttendanceView from './Studentfile/StudentAttendanceView';
import StudentTimetable from './Studentfile/StudentTimetable';
import SubjectsManagement from './components/SubjectsManagement';
import OwnerLogin from './Ownerfiles/OwnerLogin';
import OwnerDashboard from './Ownerfiles/OwnerDashboard';
import OwnerPayments from './Ownerfiles/OwnerPayments';
import OwnerSubscriptions from './Ownerfiles/OwnerSubscriptions';
import PaymentGateways from './Ownerfiles/PaymentGateways';
import SubscriptionPage from './components/SubscriptionPage';
import OwnerResource from './Ownerfiles/OwnerResource';
import SystemConfiguration from './Ownerfiles/SystemConfiguration';
import LandingPageSettings from './Ownerfiles/LandingPageSettings';
import OwnerSchoolWebsite from './Ownerfiles/OwnerSchoolWebsite';
import AIControl from './Ownerfiles/AIControl';
import SupportTicketsManager from './Ownerfiles/SupportTicketsManager';
import OwnerLiveSupport from './Ownerfiles/OwnerLiveSupport';
import FinanceManagement from './components/finance/FinanceManagement';
import AcademicsContent from './components/AcademicsManagement';
import TeacherTimetable from './Teacherdashboard/TeacherTimetable';
import TeacherGrading from './Teacherdashboard/TeacherGrading';
import TeacherSchoolCalendar from './Teacherdashboard/TeacherSchoolCalendar';
import GuardianSchoolCalendar from './Parentdashboard/GuardianSchoolCalendar';
import StudentSchoolCalendar from './Studentfile/StudentSchoolCalendar';
import GroupsManagement from './Teacherdashboard/GroupsManagement';
import LibraryManagement from './components/LibraryManagement';
import LabManagement from './components/Labsmanagement/LabManagement';
import DutyRosterMaker from './components/TeacherDutyroaster/DutyRosterMaker';
import TeacherDutyRosterViewer from './Teacherdashboard/Teacherdutyroaster/TeacherDutyRosterViewer';
import TeacherExamViewer from "./Teacherdashboard/Examtimetable/TeacherExamViewer";
import TeacherLabViewer from "@/Teacherdashboard/Teacherlabs/TeacherLabViewer";
import ExamTimetable from './components/Examtimetable/ExamTimetable';
import Research from './components/Research';
import ChatbotPage from './components/ChatbotPage';
import { ResourceDashboard } from './components/ResourceDashboard';
import GuardianFinance from './Parentdashboard/GuardianFinance';
import SubscriptionExpired from './components/SubscriptionExpired';
import ErrorPage from './components/error';
import SupportPage from './components/SupportPage';
import DataPrefetcher from './components/DataPrefetcher';
import RegistrationManagement from './components/RegistrationManagement';
import TeacherAcademicDashboard from './Teacherdashboard/TeacherAcademicDashboard';
import ImpersonationBanner from './components/ImpersonationBanner';
import VolunteerLogin from './components/authentication/VolunteerLogin';
import VolunteerDashboard from './components/authentication/VolunteerDashboard';
import AdmissionNumberSetup from './components/AdmissionNumberSetup';
import RootRedirect from './components/RootRedirect';
import { useAuthRecovery } from './hooks/useAuthRecovery';
import TeachRouter from './Teacherdashboard/independent/TeachRouter';
import TestCaseSimulation from './components/TestCaseSimulation';

// Encryption utilities (Web Crypto API)
const deriveKey = async (uid: string): Promise<CryptoKey> => {
  const encoder = new TextEncoder();
  const salt = encoder.encode('static-salt-for-demo');
  const baseKey = encoder.encode(uid + ':auth-key');
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    baseKey,
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

const encryptData = async (data: string, key: CryptoKey): Promise<string> => {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(data)
  );
  const ivHex = Array.from(iv)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  const encryptedHex = Array.from(new Uint8Array(encrypted))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return `${ivHex}:${encryptedHex}`;
};

const decryptData = async (encryptedData: string, key: CryptoKey): Promise<string> => {
  const [ivHex, encryptedHex] = encryptedData.split(':');
  if (!ivHex || !encryptedHex) throw new Error('Invalid encrypted data format');
  const iv = new Uint8Array(ivHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
  const encrypted = new Uint8Array(encryptedHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted
  );
  return new TextDecoder().decode(decrypted);
};

// Define interfaces for type safety
interface Affiliation {
  role: string;
  schoolID: string;
}

interface UserState {
  id: string;
  email: string | null;
  firstName: string;
  lastName: string;
  selectedAffiliation: Affiliation | null;
  affiliations: Affiliation[];
  subscriptionStatus?: 'active' | 'trial' | 'paused' | 'expired' | 'inactive' | 'none' | 'unknown' | 'not_registered';
  subscriptionExpiry?: number;
}

interface CachedAuth {
  uid: string;
  user: UserState;
  idToken: string;
  refreshToken: string;
  expiry: number;
}

// SettingsPage component
const SettingsPage = () => {
  const navigate = useNavigate();
  return (
    <SettingsContent
      onNavigateToSchoolRegistration={() => navigate('/dashboard/registerschool')}
      onNavigateToEditSchool={() => navigate('/dashboard/editschool')}
    />
  );
};

// SchoolRegistrationPage component
const SchoolRegistrationPage = () => {
  const navigate = useNavigate();
  return (
    <SchoolRegistrationForm
      onBack={() => {
        console.log('SchoolRegistrationPage - Navigating to /dashboard/settings');
        navigate('/dashboard/settings');
      }}
    />
  );
};

// EditSchoolPage component
const EditSchoolPage = () => {
  const navigate = useNavigate();
  return (
    <EditSchoolDetails
      onBack={() => {
        console.log('EditSchoolPage - Navigating to /dashboard/settings');
        navigate('/dashboard/settings');
      }}
    />
  );
};

// LoginRedirect component - redirects to appropriate login based on context
const LoginRedirect = () => {
  const navigate = useNavigate();
  const usersState = useSelector(selectUsers);
  const currentUser = usersState.currentUser;
  
  useEffect(() => {
    // CRITICAL: If user is already logged in, send them to dashboard, NOT landing page
    if (currentUser && currentUser.selectedAffiliation) {
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // Check stored portal preference
    const storedPortal = localStorage.getItem('portal');
    
    // Redirect based on stored portal or default to landing page
    if (storedPortal === 'admin') {
      navigate('/admin-login', { replace: true });
    } else if (storedPortal === 'guardian') {
      navigate('/guardian-login', { replace: true });
    } else if (storedPortal === 'school_staff') {
      navigate('/faculty-login', { replace: true });
    } else if (storedPortal === 'student') {
      navigate('/student-login', { replace: true });
    } else if (storedPortal === 'owner') {
      navigate('/owner-login', { replace: true });
    } else {
      // No stored portal, go to landing page (user confirmed not logged in at this point)
      navigate('/landing-page', { replace: true });
    }
  }, [navigate, currentUser]);
  
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Redirecting...</p>
      </div>
    </div>
  );

};

const NetworkNavigationGuard = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const publicPaths = [
    '/',
    '/landing-page',
    '/home',
    '/login-page',
    '/admin-login',
    '/guardian-login',
    '/student-login',
    '/faculty-login',
    '/owner-login',
    '/privacy-policy',
    '/terms-of-service',
    '/support',
    '/error',
  ];

  // Track last non-public route so we can recover when something redirects to a public page while offline.
  useEffect(() => {
    const path = (location.pathname || '/').toLowerCase();
    if (!publicPaths.includes(path)) {
      localStorage.setItem('lastProtectedPath', path);
    }
  }, [location.pathname]);

  // ENHANCED PROTECTION: Prevent authenticated users from reaching landing/login pages
  // This acts as a safety net even when online - if we have cached auth, don't go to landing
  useEffect(() => {
    const path = (location.pathname || '/').toLowerCase();
    
    // Only intercept if we're on a public path (landing, login pages, etc.)
    if (!publicPaths.includes(path)) return;
    
    // Skip login pages - users might legitimately visit these to switch accounts
    const isLoginPage = ['/admin-login', '/guardian-login', '/student-login', '/faculty-login', '/owner-login'].includes(path);
    
    // Check if we have valid cached auth
    const cachedAuth = localStorage.getItem('cachedAuth');
    if (cachedAuth && !isLoginPage) {
      try {
        const parsed = JSON.parse(cachedAuth);
        if (parsed && parsed.uid) {
          // We have cached auth - user shouldn't be on landing page
          const lastProtectedPath = (localStorage.getItem('lastProtectedPath') || '').toLowerCase();
          const fallback = '/dashboard';
          const target = lastProtectedPath && !publicPaths.includes(lastProtectedPath) ? lastProtectedPath : fallback;
          
          console.log(`NetworkNavigationGuard: User has cached auth but is on ${path}. Redirecting to ${target}`);
          navigate(target, { replace: true });
          return;
        }
      } catch (e) {
        // Parsing failed, continue normally
      }
    }
    
    // Original offline-mode protection
    if (!enabled) return;

    const lastProtectedPath = (localStorage.getItem('lastProtectedPath') || '').toLowerCase();
    const fallback = '/dashboard';
    const target = lastProtectedPath && !publicPaths.includes(lastProtectedPath) ? lastProtectedPath : fallback;

    if (path !== target) {
      navigate(target, { replace: true });
    }
  }, [enabled, location.pathname, navigate]);

  return null;
};

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  
  const usersState = useSelector(selectUsers);
  const subscriptionStatus = usersState.currentUser?.subscriptionStatus;
  const [user, setAuthUser] = useState<User | null>(null);
  const [selectedAffiliation, setSelectedAffiliation] = useState<Affiliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNetworkError, setIsNetworkError] = useState(!navigator.onLine);
  
  // HOSTNAME DETECTION: Check if we are on the "teach" subdomain
  const hostname = window.location.hostname;
  const isTeachPlatform = hostname.includes('teach.');

  // REINFORCEMENT: Auth state recovery hook - monitors and recovers from auth flickers
  useAuthRecovery();


  useEffect(() => {
    if (isTeachPlatform) return;
    const auth = getAuth();

    // Set Firebase persistence to LOCAL
    setPersistence(auth, browserLocalPersistence).catch(err => {
      console.error('App - Failed to set persistence:', err);
    });

    // Proactively restore from cache on mount
    const restoreFromCache = async () => {
      const encryptedAuth = localStorage.getItem('cachedAuth');
      if (encryptedAuth) {
        try {
          const cachedData = JSON.parse(encryptedAuth) as { uid: string; data: string };
          const key = await deriveKey(cachedData.uid);
          const decryptedAuth = await decryptData(cachedData.data, key);
          const { user: cachedUser, expiry } = JSON.parse(decryptedAuth) as CachedAuth;
          if (Date.now() < expiry + 30 * 60 * 1000) {
            dispatch(setUser(cachedUser));
            setAuthUser({ uid: cachedUser.id, email: cachedUser.email } as User);
            setSelectedAffiliation(cachedUser.selectedAffiliation);
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] App - Restored from cache`);
            return true;
          } else {
            console.log('App - Cached token expired');
            localStorage.removeItem('cachedAuth');
            dispatch(clearAuthCache());
          }
        } catch (err) {
          console.error('App - Cache restoration failed:', err);
        }
      }
      return false;
    };

    // Attempt cache restoration immediately
    restoreFromCache().then((restored) => {
      const unsubscribe = onAuthStateChanged(auth, async (currentUser: User | null) => {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] App - onAuthStateChanged - User:`, currentUser?.uid, 'Email:', currentUser?.email);
        let authResolved = false;
        const timeoutId = setTimeout(() => {
          if (authResolved) return;
          console.error('App - Timeout: Failed to resolve auth state within 10 seconds');
          if (!navigator.onLine) {
            console.log('App - Timeout due to offline status');
            setIsNetworkError(true);
            if (restored) {
              setLoading(false); // Keep using cached data if offline
            }
            // If not restored, we stay in loading/network error state (PageLoader will show)
          } else {
            // If online but timed out, we might want to show an error or just let it fail to landing
            // But if it's just slow, maybe we should set network error?
            console.log('App - Timeout but online. Potential slow connection.');
            // If we have no user, this will redirect to landing. 
            // If the user IS logged in but Firebase is slow, this is bad.
            // Let's assume if it takes 10s, something is wrong with the connection.
            setIsNetworkError(true); 
          }
        }, 10000); // Increased timeout to 10 seconds

        if (currentUser) {
          try {
            const idTokenResult = await currentUser.getIdTokenResult(false);
            const idToken = await currentUser.getIdToken();
            const refreshToken = currentUser.refreshToken;

            interface CustomClaims {
              affiliations?: Array<{ role: string; schoolID?: string }>;
              defaultAffiliation?: { role: string; schoolID?: string };
            }

            const claims = idTokenResult.claims as CustomClaims;
            const rawAffiliations = claims.affiliations || [];
            const affiliations: Affiliation[] = rawAffiliations.map(aff => ({
              role: String(aff.role ?? ''),
              schoolID: String(aff.schoolID ?? ''),
            }));

            const rawDefaultAff = claims.defaultAffiliation || null;
            const defaultAffiliation: Affiliation | null = rawDefaultAff
              ? { role: String(rawDefaultAff.role ?? ''), schoolID: String(rawDefaultAff.schoolID ?? '') }
              : null;

            console.log('App - Affiliations:', affiliations, 'Default Affiliation:', defaultAffiliation);

            const storedPortal = localStorage.getItem('portal');
            const pathname = window.location.pathname.toLowerCase();
            console.log('App - Pathname:', pathname, 'Stored Portal:', storedPortal);

            let portal: string | null = null;
            if (pathname.includes('/owner-login')) portal = 'owner';
            else if (pathname.includes('/login-page') || pathname.includes('/admin-login')) portal = 'admin';
            else if (pathname.includes('/school-staff-sign-up-page') || pathname.includes('/dashboard/schoolstaff') || pathname.includes('/enrolltoaschool')) portal = 'school_staff';
            else if (pathname.includes('/guardian-login')) portal = 'guardian';
            else if (pathname.includes('/guardian-dashboard')) portal = 'guardian';
            else if (pathname.includes('/student-dashboard')) portal = 'student';
            else if (pathname.includes('/admin-dashboard')) portal = 'admin';
            else if (pathname.includes('/faculty-dashboard')) portal = 'school_staff';
            else if (pathname.includes('/owner-dashboard')) portal = 'owner';
            else portal = storedPortal;

            let selected: Affiliation | null = null;
            if (affiliations.length > 0) {
              // PRIORITY 1: If defaultAffiliation matches the requested portal, use it
              // This ensures Cloud Function-set defaultAffiliation is respected
              if (defaultAffiliation && defaultAffiliation.role === portal && defaultAffiliation.schoolID) {
                selected = { ...defaultAffiliation };
                console.log('App - Using defaultAffiliation for portal:', portal);
              } 
              // PRIORITY 2: Find a matching affiliation for the portal
              else if (portal) {
                const matchForPortal = affiliations.find(aff => aff.role === portal && aff.schoolID);
                if (matchForPortal) {
                  selected = { ...matchForPortal };
                } else {
                  // Special handling: admin can access school_staff routes (admin is superset)
                  if (portal === 'school_staff') {
                    const adminAff = affiliations.find(aff => aff.role === 'admin' && aff.schoolID);
                    if (adminAff) {
                      selected = { ...adminAff };
                      console.log('App - Admin accessing school_staff portal, using admin affiliation');
                    } else {
                      selected = defaultAffiliation || affiliations[0];
                    }
                  } else if (portal === 'admin') {
                    selected = affiliations.find(aff => aff.role === 'admin') || null;
                  } else {
                    selected = defaultAffiliation || affiliations[0];
                  }
                }
              } 
              // PRIORITY 3: No portal specified, use defaultAffiliation or first affiliation
              else {
                selected = defaultAffiliation || affiliations[0];
              }
            }

            if (!selected) {
              console.error('App - No valid affiliation found for user:', currentUser.uid);
              // If the user is currently on a login route, don't sign them out here.
              // Allow the specific login page to handle claim setup (setClaims, setDefaultAffiliation) and redirect.
              const isOnLoginRoute = (
                pathname.includes('/admin-login') ||
                pathname.includes('/guardian-login') ||
                pathname.includes('/student-login') ||
                pathname.includes('/owner-login') ||
                pathname.includes('/faculty-login') ||
                pathname.includes('/school-staff-sign-up-page')
              );
              if (isOnLoginRoute) {
                console.log('App - On a login route with no valid affiliation; deferring to login flow without signing out.');
                setLoading(false);
                authResolved = true;
                clearTimeout(timeoutId);
                return;
              }
              // Otherwise, clear state and sign out as before
              await auth.signOut();
              localStorage.removeItem('cachedAuth');
              dispatch(clearAuthCache());
              setAuthUser(null);
              setSelectedAffiliation(null);
              dispatch(setUser(null));
              setLoading(false);
              authResolved = true;
              clearTimeout(timeoutId);
              return;
            }

            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.exists() ? (userDoc.data() as { firstName?: string; lastName?: string }) : { firstName: 'N/A', lastName: 'N/A' };

            let subscriptionStatus: 'active' | 'trial' | 'paused' | 'expired' | 'none' | 'not_registered' | undefined;
            let subscriptionExpiry: number | undefined;
            if (selected && selected.schoolID) {
              try {
                const schoolDetailsRef = doc(db, `schools/${selected.schoolID}/schoolDetails/main`);
                const schoolDetailsDoc = await getDoc(schoolDetailsRef);
                if (schoolDetailsDoc.exists()) {
                  const data = schoolDetailsDoc.data();
                  
                  // Check if the school is actually fully registered (has a name)
                  if (!data.name || data.name.trim() === '') {
                    subscriptionStatus = 'not_registered';
                  } else {
                    subscriptionStatus = data.subscriptionStatus ? (data.subscriptionStatus as 'active' | 'trial' | 'paused' | 'expired') : 'none';
                  }
                  
                  // Get expiry timestamp
                  if (data.subscriptionExpiry && typeof data.subscriptionExpiry.toMillis === 'function') {
                    subscriptionExpiry = data.subscriptionExpiry.toMillis();
                  }
                  
                  // AUTO-EXPIRY CHECK
                  // WARNING: We previously auto-reverted to 'expired' here, but this causes a fight with the Owner 'Resume' action
                  // if the expiry date is in the past. For now, we allow access if status is 'active' even if date is past,
                  // trusting the Owner's manual 'Resume' action is authoritative.
                  if ((subscriptionStatus === 'active' || subscriptionStatus === 'trial') && subscriptionExpiry && Date.now() > subscriptionExpiry) {
                    console.warn('App - Subscription date is past, but status is Active. Retaining Active status to allow admin access.');
                    // We DO NOT auto-update to 'expired' here anymore to prevent the loop.
                    // The Owner must manually update the date or status in the dashboard.
                  }
                } else {
                  subscriptionStatus = 'not_registered';
                }
              } catch (err: any) {
                console.error('App - Error fetching subscription status:', err);
                // If we get an error (e.g., permission-denied during claim propagation),
                // fallback to 'not_registered' so the user is redirected to the registration
                // page where it will automatically retry fetching the document.
                subscriptionStatus = 'not_registered';
              }
            } else if (selected && selected.role === 'admin') {
              subscriptionStatus = 'not_registered';
            }

            const userPayload: UserState = {
              id: currentUser.uid,
              email: currentUser.email,
              selectedAffiliation: selected,
              affiliations,
              firstName: userData.firstName || currentUser.displayName?.split(' ')[0] || 'N/A',
              lastName: userData.lastName || currentUser.displayName?.split(' ')[1] || 'N/A',
              subscriptionStatus,
              subscriptionExpiry,
            };

            // Encrypt and cache auth data
            const key = await deriveKey(currentUser.uid);
            const expRaw = idTokenResult.claims.exp ?? 0;
            const expNumber = typeof expRaw === 'number' ? expRaw : Number(expRaw) || 0;
            const expiry = expNumber * 1000;
            const authData = JSON.stringify({
              uid: currentUser.uid,
              user: userPayload,
              idToken,
              refreshToken,
              expiry,
            });
            const encryptedAuth = await encryptData(authData, key);
            localStorage.setItem('cachedAuth', JSON.stringify({
              uid: currentUser.uid,
              data: encryptedAuth,
            }));

            // REINFORCEMENT: Also set sessionStorage quickAuth for even faster recovery
            // This is a lightweight backup that doesn't require decryption
            sessionStorage.setItem('quickAuth', JSON.stringify({
              uid: currentUser.uid,
              role: selected.role,
              schoolID: selected.schoolID,
              timestamp: Date.now(),
            }));

            dispatch(setUser(userPayload));
            setAuthUser(currentUser);
            setSelectedAffiliation(selected);
            setIsNetworkError(false);
            setLoading(false);
          } catch (error: any) {
            console.error('App - Error fetching auth claims or user data:', error);
            
            // Enhanced network error detection
            const isConnectivityError = 
              error.message?.toLowerCase().includes('offline') || 
              error.code === 'auth/network-request-failed' ||
              error.code === 'unavailable' || // Firestore offline
              error.message?.includes('Failed to fetch') ||
              error.message?.includes('Network Error') ||
              !navigator.onLine;

            if (isConnectivityError) {
              console.log('App - Offline or network error, using cached auth if available, but NOT signing out');
              setIsNetworkError(true);
              if (restored) {
                setLoading(false); // Continue with cached data
              } else {
                const encryptedAuth = localStorage.getItem('cachedAuth');
                if (encryptedAuth) {
                  try {
                    const cachedData = JSON.parse(encryptedAuth) as { uid: string; data: string };
                    const key = await deriveKey(cachedData.uid);
                    const decryptedAuth = await decryptData(cachedData.data, key);
                    const { user: cachedUser, expiry } = JSON.parse(decryptedAuth) as CachedAuth;
                    if (Date.now() < expiry + 30 * 60 * 1000) {
                      dispatch(setUser(cachedUser));
                      setAuthUser({ uid: cachedUser.id, email: cachedUser.email } as User);
                      setSelectedAffiliation(cachedUser.selectedAffiliation);
                      setLoading(false);
                      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] App - Restored from cache (network error)`);
                    } else {
                      console.log('App - Cached token expired');
                      localStorage.removeItem('cachedAuth');
                      dispatch(clearAuthCache());
                      setAuthUser(null);
                      setSelectedAffiliation(null);
                      dispatch(setUser(null));
                      setLoading(false);
                    }
                  } catch (decryptError) {
                    console.error('App - Failed to decrypt cached auth:', decryptError);
                    setAuthUser(null);
                    setSelectedAffiliation(null);
                    dispatch(setUser(null));
                    setLoading(false);
                  }
                } else {
                  // Do NOT sign out or clear user on network error, just show network error state
                  setLoading(false);
                }
              }
            } else {
              console.log('App - Non-network error, signing out');
              await auth.signOut();
              localStorage.removeItem('cachedAuth');
              dispatch(clearAuthCache());
              setAuthUser(null);
              setSelectedAffiliation(null);
              dispatch(setUser(null));
              setLoading(false);
            }
          }
        } else {
          console.log('App - No user authenticated');
          
          // CRITICAL FIX: Firebase can temporarily fire null during token refresh.
          // If we have cached auth (regardless of online status), we should NOT immediately
          // clear the user. Instead, we debounce/wait to see if Firebase recovers.
          const encryptedAuth = localStorage.getItem('cachedAuth');
          
          if (restored) {
            console.log('App - Keeping cached user despite null auth state (potential network issue or token refresh)');
            setLoading(false); // Continue with cached data
          } else if (encryptedAuth) {
            // We have cached auth - this might be a temporary Firebase flicker
            try {
              const cachedData = JSON.parse(encryptedAuth) as { uid: string; data: string };
              const key = await deriveKey(cachedData.uid);
              const decryptedAuth = await decryptData(cachedData.data, key);
              const { user: cachedUser, expiry } = JSON.parse(decryptedAuth) as CachedAuth;
              
              if (Date.now() < expiry + 30 * 60 * 1000) {
                // Cache is still valid - this is likely a Firebase auth state flicker
                // Keep the user logged in with cached data
                console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] App - Null auth state but valid cache exists. Keeping user logged in (potential token refresh flicker).`);
                dispatch(setUser(cachedUser));
                setAuthUser({ uid: cachedUser.id, email: cachedUser.email } as User);
                setSelectedAffiliation(cachedUser.selectedAffiliation);
                setLoading(false);
                authResolved = true;
                clearTimeout(timeoutId);
                
                // Set up a safety net: if Firebase doesn't restore auth within 5 seconds,
                // AND we're definitely online, attempt to force refresh the token
                if (navigator.onLine) {
                  setTimeout(async () => {
                    const auth = getAuth();
                    if (!auth.currentUser) {
                      console.log('App - Firebase still has no user after 5s. Attempting token refresh...');
                      // Try to silently re-sign-in using the cached refresh token
                      // This is a last-resort recovery mechanism
                    }
                  }, 5000);
                }
                return;
              } else {
                console.log('App - Cached token expired');
                localStorage.removeItem('cachedAuth');
                dispatch(clearAuthCache());
              }
            } catch (err) {
              console.error('App - Failed to restore cache on no user:', err);
            }
            
            // Cache was expired or invalid - proceed with logout
            localStorage.removeItem('cachedAuth');
            dispatch(clearAuthCache());
            setAuthUser(null);
            setSelectedAffiliation(null);
            dispatch(setUser(null));
            setLoading(false);
          } else {
            // No cached auth at all - user is truly not logged in
            setAuthUser(null);
            setSelectedAffiliation(null);
            dispatch(setUser(null));
            setLoading(false);
          }
        }
        authResolved = true;
        clearTimeout(timeoutId);
      });

      const handleOnline = () => {
        if (isNetworkError) {
          console.log('App - Network restored');
          setIsNetworkError(false);
          if (auth.currentUser) {
            auth.currentUser.getIdToken(true).catch(err => console.error('App - Failed to refresh token:', err));
          }
        }
      };
      const handleOffline = () => {
        console.log('App - Network lost');
        setIsNetworkError(true);
      };

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        console.log('App - Unsubscribing from onAuthStateChanged');
        unsubscribe();
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    });

    return () => {};
  }, [dispatch]);

  // Real-time role change listener
  useEffect(() => {
    if (isTeachPlatform) return;
    if (!user || !selectedAffiliation?.schoolID) return;

    // Listen to the user's own document
    const userRef = doc(db, 'users', user.uid);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log(`App - Setting up role change listener for user: ${user.uid}`);
    }

    const unsubscribe = onSnapshot(userRef, async (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const remoteAffiliations: Affiliation[] = data.affiliations || [];
      
      // Find the remote role corresponding to the current school
      const relevantRemoteAff = remoteAffiliations.find(
        (a) => a.schoolID === selectedAffiliation.schoolID
      );

      // Check if this is an intentional admin<->staff view switch
      // If the user has both admin and school_staff for the same school, 
      // switching between them is intentional and should NOT trigger a reload
      const hasAdminForSchool = remoteAffiliations.some(
        (a) => a.role === 'admin' && a.schoolID === selectedAffiliation.schoolID
      );
      const hasStaffForSchool = remoteAffiliations.some(
        (a) => a.role === 'school_staff' && a.schoolID === selectedAffiliation.schoolID
      );
      const isIntentionalViewSwitch = hasAdminForSchool && hasStaffForSchool && 
        (selectedAffiliation.role === 'admin' || selectedAffiliation.role === 'school_staff');

      // If the role in the DB is different from what we currently have in state
      // AND it's not just an intentional admin/staff view switch
      if (relevantRemoteAff && relevantRemoteAff.role !== selectedAffiliation.role && !isIntentionalViewSwitch) {
        console.log(`App - External role change detected (Local: ${selectedAffiliation.role}, Remote: ${relevantRemoteAff.role}). Refreshing session...`);
        
        try {
            // Force token refresh to get updated custom claims
            await getAuth().currentUser?.getIdToken(true);
            
            // Force a hard reload/redirect to root to ensure clean state and rerouting
            window.location.href = '/'; 
        } catch (err) {
            console.error("App - Failed to refresh token after role change:", err);
        }
      }
    });

    return () => unsubscribe();
  }, [user, selectedAffiliation]);

  // Real-time subscription listener
  useEffect(() => {
    if (isTeachPlatform) return;
    if (!user || !selectedAffiliation?.schoolID) return;

    // Only listen if role is NOT owner (owners don't have subscriptions)
    if (selectedAffiliation.role === 'owner') return;

    const schoolRef = doc(db, `schools/${selectedAffiliation.schoolID}/schoolDetails/main`);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log(`App - Setting up subscription listener for school: ${selectedAffiliation.schoolID}`);
    }

    // Track previous status for detecting payment/renewal
    const previousStatus = subscriptionStatus;

    const unsubscribe = onSnapshot(schoolRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // REGISTRATION DETECTION: If the school now has a name, it has been registered.
        // The initial document created at sign-up has no name. SchoolRegistrationForm.onSubmit writes the name.
        // We must promote 'not_registered' to the real status once a name exists.
        const isFullyRegistered = data.name && data.name.trim() !== '';
        let newStatus: 'active' | 'trial' | 'paused' | 'expired' | 'none' | 'not_registered' | undefined;

        if (!isFullyRegistered) {
          newStatus = 'not_registered';
        } else {
          // Default to 'none' when subscriptionStatus field is absent (new school, no plan yet)
          // This is consistent with onAuthStateChanged (line ~549) and prevents ProtectedRoute
          // from receiving `undefined` and rendering a black screen on /dashboard/subscription.
          newStatus = (data.subscriptionStatus as 'active' | 'trial' | 'paused' | 'expired' | 'none' | undefined) ?? 'none';
        }

        let newExpiry: number | undefined;
        if (data.subscriptionExpiry && typeof data.subscriptionExpiry.toMillis === 'function') {
          newExpiry = data.subscriptionExpiry.toMillis();
        } else if (typeof data.subscriptionExpiry === 'number') {
          newExpiry = data.subscriptionExpiry;
        }
        
        if (process.env.NODE_ENV !== 'production') {
          console.log(`App - Real-time subscription update: Status=${newStatus}, Expiry=${newExpiry}, FullyRegistered=${isFullyRegistered}`);
        }
        
        // Dispatch update to Redux
        dispatch(updateSubscriptionStatus({ status: newStatus, expiry: newExpiry }));

        // REGISTRATION COMPLETE: If status just moved out of not_registered,
        // redirect to subscription page so admin can start a plan.
        if (isFullyRegistered && previousStatus === 'not_registered' && newStatus !== 'not_registered') {
          console.log('App - School registration detected. Redirecting to subscription page...');
          // Small delay so Firestore write fully propagates before navigation
          setTimeout(() => {
            window.location.assign('/dashboard/subscription');
          }, 1000);
        }
      }
    }, (error) => {
      console.error("App - Error listening to subscription changes:", error);
    });

    return () => {
      if (process.env.NODE_ENV !== 'production') {
        console.log("App - Unsubscribing from subscription listener");
      }
      unsubscribe();
    };
  }, [user, selectedAffiliation, dispatch, subscriptionStatus]);

  useEffect(() => {
    if (isTeachPlatform) return;
    if (isNetworkError && user) {
      toast({
        title: "Offline Mode",
        description: "You are currently offline. Some features may be unavailable.",
        variant: "destructive",
        duration: 5000,
      });
    }
  }, [isNetworkError, user, toast]);

  if (!isTeachPlatform && (loading || (isNetworkError && !user))) {
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] App - Render - Loading or offline (no user), showing PageLoader`);
    return <PageLoader isContentReady={false} onNetworkErrorChange={setIsNetworkError}>{null}</PageLoader>;
  }

  // If network error but we have a user, show a toast (optional) or just let them proceed
  if (isNetworkError && user) {
    console.log('App - Render - Offline but user cached, rendering app');
    // We can't call toast here directly in render, use useEffect instead
  }

  console.log('App - Render - Current User:', user, 'Selected Affiliation:', selectedAffiliation);

  const isValidRole = selectedAffiliation && ['admin', 'guardian', 'school_staff', 'student', 'owner'].includes(selectedAffiliation.role);



  if (isTeachPlatform) {
    return (
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <TeachRouter />
        <Toaster />
      </Router>
    );
  }

  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <ImpersonationBanner />
      <NetworkNavigationGuard enabled={Boolean(isNetworkError && user)} />
      <DataPrefetcher />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/landing-page" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/login-page" element={<LoginRedirect />} />
        <Route path="/admin-login" element={<GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}><Authe /></GoogleReCaptchaProvider>} />
        <Route path="/faculty-login" element={user && selectedAffiliation?.role === 'school_staff' ? <Navigate to="/dashboard" replace /> : <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}><Signup /></GoogleReCaptchaProvider>} />
        <Route path="/guardian-login" element={<GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}><GuardianLogin /></GoogleReCaptchaProvider>} />
        <Route path="/student-login" element={<GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}><StudentLogin /></GoogleReCaptchaProvider>} />
        <Route path="/owner-login" element={user && selectedAffiliation?.role === 'owner' ? <Navigate to="/dashboard" replace /> : <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}><OwnerLogin /></GoogleReCaptchaProvider>} />
        <Route path="/School-admin-sign-up" element={<AdminRegistrationForm />} />
        <Route path="/volunteer-access/:inviteCode" element={<GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}><VolunteerLogin /></GoogleReCaptchaProvider>} />
        <Route path="/volunteer-dashboard" element={<VolunteerDashboard />} />
        <Route path="/portal-selection" element={<RootRedirect />} />
        {/* Standalone admin registration route — OUTSIDE the auth gate so it survives claim propagation delays */}
        <Route
          path="/dashboard/registerschool"
          element={
            // If user is authenticated as admin (even before Redux isValidRole is set), allow access.
            // This prevents the black screen when Firebase claims haven't propagated yet.
            user || localStorage.getItem('portal') === 'admin'
              ? <ProtectedRoute allowedRole="admin"><SchoolRegistrationPage /></ProtectedRoute>
              : <Navigate to="/admin-login" replace />
          }
        />

        <Route
          path="/subscription-expired"
          element={
            user && selectedAffiliation?.role && (
              !subscriptionStatus || 
              ['expired', 'paused', 'inactive', 'none', 'unknown', 'not_registered'].includes(subscriptionStatus) ||
              (usersState.currentUser?.subscriptionExpiry && Date.now() > usersState.currentUser.subscriptionExpiry)
            )
              ? (() => {
                  // not_registered admins belong in registration, not the expired page
                  if (subscriptionStatus === 'not_registered' && selectedAffiliation?.role === 'admin') {
                    return <Navigate to="/dashboard/registerschool" replace />;
                  }
                  return <SubscriptionExpired />;
                })()
              : <Navigate to={user ? '/dashboard' : '/login-page'} replace />
          }
        />

        {user && isValidRole ? (
          <>
            {/* Admin Routes - Always rendered but protected */}
            <Route path="/admin-dashboard" element={<ProtectedRoute allowedRole="admin"><Layout /></ProtectedRoute>} />
            <Route path="/dashboard/editschool" element={<ProtectedRoute allowedRole="admin"><EditSchoolPage /></ProtectedRoute>} />
            <Route path="/classes" element={<ProtectedRoute allowedRole="admin"><ClassesManagement /></ProtectedRoute>} />

            <Route path="/dashboard/academics" element={
              <RoleBasedRoute roleComponents={{
                admin: <ProtectedRoute allowedRole="admin"><AcademicsContent /></ProtectedRoute>,
                school_staff: <ProtectedRoute allowedRole="school_staff"><TeacherAcademicDashboard /></ProtectedRoute>
              }} />
            } />
            <Route path="/lessons" element={<ProtectedRoute allowedRole="admin"><SubjectsManagement /></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute allowedRole="admin"><LibraryManagement /></ProtectedRoute>} />
            <Route path="/students" element={<ProtectedRoute allowedRole="admin"><StudentsManagement /></ProtectedRoute>} />
            <Route path="/research" element={<ProtectedRoute allowedRole="admin"><Research /></ProtectedRoute>} />
            <Route path="/explore" element={<ProtectedRoute allowedRole="admin"><ResourceDashboard /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute allowedRole="admin"><AdminAttendanceAnalytics /></ProtectedRoute>} />
            <Route path="/dashboard/Staff" element={<ProtectedRoute allowedRole="admin"><TeachersManagement /></ProtectedRoute>} />
            <Route path="/communication" element={<ProtectedRoute allowedRole="admin"><CommunicationManagement /></ProtectedRoute>} />
            <Route path="/dorms" element={<ProtectedRoute allowedRole="admin"><DormsManagement /></ProtectedRoute>} />
            <Route path="/finance-dashboard" element={
              <RoleBasedRoute roleComponents={{
                admin: <SubscriptionGuard><FinanceManagement /></SubscriptionGuard>,
                school_staff: <SubscriptionGuard><FinanceManagement /></SubscriptionGuard>
              }} />
            } />
            <Route path="/dashboard/subscription" element={<ProtectedRoute allowedRole="admin"><SubscriptionPage /></ProtectedRoute>} />
            <Route path="/chatbot" element={<ProtectedRoute allowedRole="admin"><ChatbotPage /></ProtectedRoute>} />
            <Route path="/lab-management" element={<ProtectedRoute allowedRole="admin"><LabManagement /></ProtectedRoute>} />
            <Route path="/duty-roster" element={<ProtectedRoute allowedRole="admin"><DutyRosterMaker /></ProtectedRoute>} />
            <Route path="/dashboard/registration-management" element={<ProtectedRoute allowedRole="admin"><RegistrationManagement /></ProtectedRoute>} />
            <Route path="/dashboard/admission-setup" element={<ProtectedRoute allowedRole="admin"><AdmissionNumberSetup /></ProtectedRoute>} />

            <Route path="/exam-timetable" element={<ProtectedRoute allowedRole="admin"><ExamTimetable /></ProtectedRoute>} />
            <Route path="/test-cases" element={<ProtectedRoute allowedRole="admin"><TestCaseSimulation /></ProtectedRoute>} />

            {/* Guardian Routes - Always rendered but protected */}
            <Route path="/guardian-dashboard" element={<ProtectedRoute allowedRole="guardian"><GuardianDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/finance" element={<ProtectedRoute allowedRole="guardian"><GuardianFinance /></ProtectedRoute>} />
            <Route path="/guardian-login" element={<Navigate to="/guardian-dashboard" replace />} />

            {/* School Staff Routes - Always rendered but protected */}
            <Route path="/faculty-dashboard" element={<ProtectedRoute allowedRole="school_staff"><Teacherdashboard /></ProtectedRoute>} />
            <Route path="/school-staff-sign-up-page" element={<Navigate to="/faculty-dashboard" replace />} />
            <Route path="/dashboard/schoolstaff" element={<ProtectedRoute allowedRole="school_staff"><Teacherdashboard /></ProtectedRoute>} />
            <Route path="/dashboard/timetable" element={<ProtectedRoute allowedRole="school_staff"><TeacherTimetable /></ProtectedRoute>} />
            <Route path="/dashboard/grading" element={<ProtectedRoute allowedRole="school_staff"><TeacherGrading /></ProtectedRoute>} />
            <Route path="/dashboard/dutyroster" element={<ProtectedRoute allowedRole="school_staff"><TeacherDutyRosterViewer /></ProtectedRoute>} />
            <Route path="/dashboard/labs" element={<ProtectedRoute allowedRole="school_staff"><TeacherLabViewer /></ProtectedRoute>} />
            <Route path="/dashboard/exam-timetable" element={<ProtectedRoute allowedRole="school_staff"><TeacherExamViewer /></ProtectedRoute>} />
            <Route path="/dashboard/school-calendar" element={
              <RoleBasedRoute roleComponents={{
                school_staff: <SubscriptionGuard><TeacherSchoolCalendar /></SubscriptionGuard>,
                guardian: <SubscriptionGuard><GuardianSchoolCalendar /></SubscriptionGuard>,
                student: <SubscriptionGuard><StudentSchoolCalendar /></SubscriptionGuard>
              }} />
            } />
            <Route path="/dashboard/library" element={<ProtectedRoute allowedRole="school_staff"><LibrarianRoute><TeacherLibraryManagement /></LibrarianRoute></ProtectedRoute>} />
            <Route path="/dashboard/explore" element={<ProtectedRoute allowedRole="school_staff"><ResourceDashboard layoutType="teacher" /></ProtectedRoute>} />
            <Route path="/dashboard/research" element={<ProtectedRoute allowedRole="school_staff"><Research layoutType="teacher" /></ProtectedRoute>} />
            <Route path="/groups-management" element={<ProtectedRoute allowedRole="school_staff"><GroupsManagement /></ProtectedRoute>} />
            <Route path="/dashboard/enrolltoaschool" element={<ProtectedRoute allowedRole="school_staff"><Enrolltoaschool /></ProtectedRoute>} />
            <Route path="/dashboard/communication/messageallteachers" element={<ProtectedRoute allowedRole="school_staff"><Messageallteachers /></ProtectedRoute>} />

            {/* Student Routes - Always rendered but protected */}
            <Route path="/student-dashboard" element={<ProtectedRoute allowedRole="student"><StudentDashboard /></ProtectedRoute>} />
            <Route path="/student-login" element={<Navigate to="/student-dashboard" replace />} />

            {/* Owner Routes - Always rendered but protected */}
            <Route path="/owner-dashboard" element={<ProtectedRoute allowedRole="owner"><OwnerDashboard /></ProtectedRoute>} />
            <Route path="/owner/subscriptions" element={<ProtectedRoute allowedRole="owner"><OwnerSubscriptions /></ProtectedRoute>} />
            <Route path="/owner/payments" element={<ProtectedRoute allowedRole="owner"><OwnerPayments /></ProtectedRoute>} />
            <Route path="/owner/payment-gateways" element={<ProtectedRoute allowedRole="owner"><PaymentGateways /></ProtectedRoute>} />
            <Route path="/owner/resources" element={<ProtectedRoute allowedRole="owner"><OwnerResource /></ProtectedRoute>} />
            <Route path="/owner/system-config" element={<ProtectedRoute allowedRole="owner"><SystemConfiguration /></ProtectedRoute>} />
            <Route path="/owner/landing-settings" element={<ProtectedRoute allowedRole="owner"><LandingPageSettings /></ProtectedRoute>} />
            <Route path="/owner/school-website" element={<ProtectedRoute allowedRole="owner"><OwnerSchoolWebsite /></ProtectedRoute>} />
            <Route path="/owner/school-website-preview" element={<ProtectedRoute allowedRole="owner"><SchoolWebsitePreview /></ProtectedRoute>} />
            <Route path="/owner/ai-control" element={<ProtectedRoute allowedRole="owner"><AIControl /></ProtectedRoute>} />
            <Route path="/owner/support" element={<ProtectedRoute allowedRole="owner"><SupportTicketsManager /></ProtectedRoute>} />
            <Route path="/owner/live-support" element={<ProtectedRoute allowedRole="owner"><OwnerLiveSupport /></ProtectedRoute>} />

            {/* Shared Routes with role-specific handling */}
            <Route path="/dashboard" element={
              <RoleBasedRoute roleComponents={{
                admin: <Navigate to="/admin-dashboard" replace />,
                guardian: <Navigate to="/guardian-dashboard" replace />,
                school_staff: <Navigate to="/faculty-dashboard" replace />,
                student: <Navigate to="/student-dashboard" replace />,
                owner: <Navigate to="/owner-dashboard" replace />
              }} />
            } />
            <Route path="/dashboard/settings" element={
              <RoleBasedRoute roleComponents={{
                admin: <SubscriptionGuard><SettingsPage /></SubscriptionGuard>,
                guardian: <SubscriptionGuard><ParentSettings /></SubscriptionGuard>,
                school_staff: <SubscriptionGuard><StaffSettings /></SubscriptionGuard>,
                student: <SubscriptionGuard><StudentSettings /></SubscriptionGuard>
              }} />
            } />
            <Route path="/dashboard/communication" element={
              <RoleBasedRoute roleComponents={{
                admin: <SubscriptionGuard><CommunicationManagement /></SubscriptionGuard>,
                guardian: <SubscriptionGuard><ParentCommunication /></SubscriptionGuard>,
                school_staff: <SubscriptionGuard><TeacherCommunication /></SubscriptionGuard>,
                student: <SubscriptionGuard><StudentCommunication /></SubscriptionGuard>
              }} />
            } />
            <Route path="/dashboard/attendance" element={
              <RoleBasedRoute roleComponents={{
                school_staff: <SubscriptionGuard><AttendanceManagement /></SubscriptionGuard>,
                student: <SubscriptionGuard><StudentAttendanceView /></SubscriptionGuard>
              }} />
            } />
            <Route path="/dashboard/class-schedule" element={
              <RoleBasedRoute roleComponents={{
                student: <SubscriptionGuard><StudentTimetable /></SubscriptionGuard>
              }} />
            } />
            <Route path="/school-alumni" element={
              <RoleBasedRoute roleComponents={{
                admin: <SchoolAlumni />,
                school_staff: <SchoolAlumni />
              }} />
             } />

            {/* Authenticated catch-all: send any unknown path to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        ) : (
          <>
            <Route
              path="*"
              element={
                isNetworkError
                  ? <PageLoader isContentReady={false} onNetworkErrorChange={setIsNetworkError}>{null}</PageLoader>
                  : <RootRedirect />
              }
            />
          </>
        )}
      </Routes>
      <Toaster />
    </Router>
  );
}

export default App;